import { useEffect, useState, type ReactNode, type ComponentType } from "react";

/**
 * DeferredTooltipProvider
 *
 * The Radix Tooltip package (`@radix-ui/react-tooltip`) is ~338KB transitively
 * and was being included in the critical-path bundle because `<TooltipProvider>`
 * wraps the entire app shell. Tooltips themselves don't render until the user
 * hovers/focuses something, so the provider can be hydrated lazily.
 *
 * This component renders children IMMEDIATELY (so the app paints without waiting
 * for the tooltip chunk) and then upgrades to wrapping them in the real
 * `TooltipProvider` once the chunk loads in idle time.
 *
 * Tooltips that mount before this provider is ready do NOT fall back to a
 * default provider — Radix v1.1 throws `Tooltip must be used within
 * TooltipProvider`. `@/components/ui/tooltip` therefore self-provides on every
 * `Tooltip` root (Radix supports nested providers), which is what makes this
 * deferral safe on cold loads. Do not remove that inner provider.

 */
export function DeferredTooltipProvider({ children }: { children: ReactNode }) {
  const [Provider, setProvider] = useState<ComponentType<{ children: ReactNode; delayDuration?: number }> | null>(
    null,
  );

  useEffect(() => {
    let cancelled = false;
    const load = () => {
      import("@/components/ui/tooltip").then((m) => {
        if (!cancelled) setProvider(() => m.TooltipProvider as ComponentType<{ children: ReactNode; delayDuration?: number }>);
      });
    };
    const ric = (window as Window & { requestIdleCallback?: (cb: () => void) => number }).requestIdleCallback;
    if (typeof ric === "function") {
      ric(load);
    } else {
      setTimeout(load, 200);
    }
    return () => {
      cancelled = true;
    };
  }, []);

  if (!Provider) return <>{children}</>;
  return <Provider delayDuration={150}>{children}</Provider>;
}
